import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useCallback } from 'react';
import axios from 'axios';
import { authApi } from '@/shared/api/authApi';
import { authStateAtom, authSyncStateAtom } from '@/entities/auth/model/auth.atom';
import { AuthMeResponse, AuthState, User } from '@/entities/auth/model/auth.types';
import {
  workspaceConfigAtom,
  viewModeAtom,
  VIEW_MODE_STORAGE_KEY,
} from '@/entities/workspace-config/model/workspace-config.atom';
import { getAvailableViewModes } from '@/entities/workspace-config';
import { hiddenTabsAtom, tabStateAtom } from '@/entities/tab/model/tab.atom';

async function identifyPostHogUser(user: User) {
  if (typeof window === 'undefined') return;

  try {
    const { default: posthog } = await import('posthog-js');
    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role,
      planner_id: user.plannerId,
    });
  } catch (error) {
    console.error('PostHog identify error:', error);
  }
}

async function resetPostHogUser() {
  if (typeof window === 'undefined') return;

  try {
    const { default: posthog } = await import('posthog-js');
    posthog.reset();
  } catch (error) {
    console.error('PostHog reset error:', error);
  }
}

/**
 * 인증 상태 읽기
 */
export function useAuthState(): AuthState {
  const [authState] = useAtom(authStateAtom);
  return authState;
}

/**
 * 인증 상태 설정
 */
export function useSetAuthState() {
  const setAuthState = useSetAtom(authStateAtom);
  return setAuthState;
}

/**
 * 인증 상태와 유틸리티 함수 제공
 */
export function useAuth() {
  const [authState, setAuthState] = useAtom(authStateAtom);
  const setWorkspaceConfig = useSetAtom(workspaceConfigAtom);
  const setViewMode = useSetAtom(viewModeAtom);
  const setHiddenTabs = useSetAtom(hiddenTabsAtom);
  const setTabState = useSetAtom(tabStateAtom);

  const setAuth = useCallback(
    (accessToken: string | null, user: User | null) => {
      setAuthState({
        accessToken,
        user,
        isAuthenticated: !!accessToken && !!user,
      });
      if (accessToken && user) void identifyPostHogUser(user);
      else void resetPostHogUser();
    },
    [setAuthState]
  );

  /**
   * 로그인 성공 후 /api/v1/auth/me 응답 전체를 반영합니다.
   * workspaceConfig, viewMode까지 함께 설정하므로
   * 권한 설정의 기본 페이지가 즉시 반영됩니다.
   */
  const initAuth = useCallback(
    (response: AuthMeResponse) => {
      setAuthState({
        accessToken: response.accessToken,
        user: response.user,
        isAuthenticated: true,
      });
      void identifyPostHogUser(response.user);
      if (response.workspaceConfig) setWorkspaceConfig(response.workspaceConfig);
      if (response.user.role) setViewMode(response.user.role);
    },
    [setAuthState, setWorkspaceConfig, setViewMode]
  );

  const clearAuth = useCallback(() => {
    setAuthState({
      accessToken: null,
      user: null,
      isAuthenticated: false,
    });
    setWorkspaceConfig(null);
    setViewMode('Planner');
    setHiddenTabs([]);
    setTabState(null);
    void resetPostHogUser();
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(VIEW_MODE_STORAGE_KEY);
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const key = window.sessionStorage.key(i);
        if (key?.startsWith('tab-histories:')) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
    }
  }, [setAuthState, setWorkspaceConfig, setViewMode, setHiddenTabs, setTabState]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  return {
    ...authState,
    setAuth,
    initAuth,
    clearAuth,
    logout,
  };
}

/**
 * 인증 동기화 상태 읽기
 */
export function useAuthSyncState() {
  const [syncState] = useAtom(authSyncStateAtom);
  return syncState;
}

/**
 * 인증 상태 동기화
 * 새로고침 시 자동으로 /api/v1/auth/me 호출하여 상태 복원
 */
export function useAuthSync() {
  const setAuthState = useSetAuthState();
  const setSyncState = useSetAtom(authSyncStateAtom);
  const setWorkspaceConfig = useSetAtom(workspaceConfigAtom);
  const setViewMode = useSetAtom(viewModeAtom);
  const [syncState] = useAtom(authSyncStateAtom);

  useEffect(() => {
    if (syncState.isInitialized) {
      return;
    }

    let isMounted = true;

    const syncAuth = async () => {
      setSyncState({ isLoading: true, isInitialized: false });

      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

      const fetchMeWithRetry = async () => {
        const maxAttempts = 3;
        const backoffMs = [0, 400, 900];
        let lastError: unknown;
        for (let i = 0; i < maxAttempts; i++) {
          if (backoffMs[i]! > 0) await sleep(backoffMs[i]!);
          try {
            return await authApi.getMe();
          } catch (error) {
            lastError = error;
            if (axios.isAxiosError(error)) {
              const status = error.response?.status;
              if (status === 401 || status === 403) throw error;
              if (status != null && status >= 400 && status < 500) throw error;
            }
            if (i === maxAttempts - 1) throw error;
          }
        }
        throw lastError;
      };

      try {
        const response = await fetchMeWithRetry();

        if (isMounted) {
          setAuthState({
            accessToken: response.accessToken,
            user: response.user,
            isAuthenticated: true,
          });
          void identifyPostHogUser(response.user);
          if (response.workspaceConfig) {
            setWorkspaceConfig(response.workspaceConfig);
          }
          const userRole = response.user.role ?? 'Planner';
          const hasPlannerId = !!response.user.plannerId;
          const availableModes = getAvailableViewModes(userRole, hasPlannerId);
          const stored =
            typeof window !== 'undefined'
              ? window.sessionStorage.getItem(VIEW_MODE_STORAGE_KEY)
              : null;
          let storedMode: typeof userRole | null = null;
          if (stored) {
            try {
              const parsed = JSON.parse(stored) as string;
              if (availableModes.includes(parsed as typeof userRole)) {
                storedMode = parsed as typeof userRole;
              }
            } catch {
              /* invalid */
            }
          }
          if (!storedMode) {
            setViewMode(userRole);
          }
          setSyncState({ isLoading: false, isInitialized: true });
        }
      } catch (error) {
        if (isMounted) {
          setAuthState({
            accessToken: null,
            user: null,
            isAuthenticated: false,
          });
          setWorkspaceConfig(null);
          setViewMode('Planner');
          if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(VIEW_MODE_STORAGE_KEY);
          }
          setSyncState({ isLoading: false, isInitialized: true });
        }
      }
    };

    syncAuth();

    return () => {
      isMounted = false;
    };
  }, [setAuthState, setSyncState, setWorkspaceConfig, setViewMode, syncState.isInitialized]);

  return syncState;
}
